// components/cart/PickUpScheduler.js
import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import tw from 'twin.macro';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import BranchSelector from './BranchSelector';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { obtenerFechasOcupadas } from 'scripts/obtenerFechasOcupadas';

const Container = tw.div`flex flex-col w-full`;
const ContainerDatePicker = tw.div`flex flex-col w-full items-center`;
const Title = tw.h2`text-2xl font-bold mb-4 self-start`;
const InfoText = tw.p`text-sm text-gray-600 mt-2`;
const Buttons = tw.div`flex justify-between`;
const SelectButton = tw.button`mt-4 px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-800 self-start`;
const NextButton = tw.button`mt-4 px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-800 self-end`;

const PickUpScheduler = ({ setStep, fechaSeleccionada, setFechaSeleccionada, tieneViandas = false }) => {
	const [fechasOcupadas, setFechasOcupadas] = useState([]);

	const isDateDisabled = useCallback((date) => {
		const formattedDate = format(date, 'yyyy-MM-dd');
		return fechasOcupadas.includes(formattedDate);
	}, [fechasOcupadas]);

	const isWeekend = useCallback((date) => {
		const day = date.getDay();
		return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
	}, []);

	const isToday = useCallback((date) => {
		const normalizedDate = new Date(date);
		normalizedDate.setHours(0, 0, 0, 0);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return normalizedDate.getTime() === today.getTime();
	}, []);

	const getNextAvailableDate = useCallback(() => {
		let date = new Date(); // Start from today
		let today = new Date();

		// Remove time from both dates for comparison
		today.setHours(0, 0, 0, 0);
		date.setHours(0, 0, 0, 0);

		// Ensure the date is not today and is not a weekend
		while (isDateDisabled(date) || isWeekend(date) || date.getTime() === today.getTime()) {
			date.setDate(date.getDate() + 1); // Move to the next day
		}

		return date;
	}, [isDateDisabled, isWeekend]);

	const sucursalId = useSelector((state) => state.carrito.sucursalId);

	useEffect(() => {
		const fetchFechasOcupadas = async () => {
			try {
				const data = await obtenerFechasOcupadas();
				setFechasOcupadas(data.fechas);
			} catch (error) {
				console.error('Error al obtener fechas ocupadas:', error);
			}
		};
		fetchFechasOcupadas();
	}, []);

	useEffect(() => {
		if (!fechaSeleccionada || isDateDisabled(fechaSeleccionada) || isWeekend(fechaSeleccionada) || isToday(fechaSeleccionada)) {
			setFechaSeleccionada(getNextAvailableDate());
		}
	}, [fechaSeleccionada, setFechaSeleccionada, isDateDisabled, isWeekend, isToday, getNextAvailableDate]);

	const handleContinuar = () => {
		if (!sucursalId) {
			toast.error('Por favor, selecciona una sucursal para continuar');
			return;
		}
		setStep(4);
	};

	return (
		<Container>
			<BranchSelector />
			<Title>{tieneViandas ? 'Selecciona la fecha de inicio' : 'Selecciona la fecha de retiro'}</Title>
			<ContainerDatePicker translate="no">
				<DatePicker
					selected={fechaSeleccionada}
					onChange={(date) => setFechaSeleccionada(date)}
					minDate={getNextAvailableDate()}
					filterDate={(date) => !isDateDisabled(date) && !isWeekend(date)}
					inline
					locale={es}
					dateFormat="dd/MM/yyyy"
				/>
				{fechaSeleccionada && (
					<InfoText>
						{tieneViandas ? 'Fecha de inicio' : 'Fecha seleccionada'}:{' '}
						{format(fechaSeleccionada, 'dd/MM/yyyy', { locale: es })}
					</InfoText>
				)}
			</ContainerDatePicker>
			<Buttons>
				<SelectButton onClick={() => setStep(2)}>Volver</SelectButton>
				<NextButton onClick={handleContinuar}>Continuar</NextButton>
			</Buttons>
		</Container>
	);
};

export default PickUpScheduler;
